import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
  ViewChild, OnInit, OnDestroy,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  Router,
  RouterModule,
} from '@angular/router';
import { AppService } from '../../services/app.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TippyDirective } from '@ngneat/helipopper';
import { UtilsService } from '../../services/utils.service';
import { ApiService } from '../../services/api.service';
import { StorageService } from '../../services/storage.service';
import { filter, map, startWith, Subscription } from 'rxjs';
import { LoaderComponent } from '../../components/loader/loader.component';
import { ConstantsService } from '../../services/constants.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-sidenav',
  imports: [
    RouterModule,
    CommonModule,
    NgSelectModule,
    FormsModule,
    TippyDirective,
    LoaderComponent
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss'
})
export class SidenavComponent implements OnInit, OnDestroy {
  @ViewChild('selectAsset', { static: true }) selectAsset!: NgSelectComponent;
  private readonly _constants = inject(ConstantsService);
  private readonly _service = inject(AppService);
  private readonly _storageService = inject(StorageService);
  private readonly _toastr = inject(ToastrService);
  private readonly _utilService = inject(UtilsService);
  private readonly _apiService = inject(ApiService);
  private readonly _router = inject(Router);
  private readonly _cdr = inject(ChangeDetectorRef);
  private routerSubscription: Subscription | null = null;

  sidenav = this._service.sideNav;
  isLoading = signal(false);
  currentRoute = signal<string | null>(null);
  selectedAsset = this._service.selectedAsset

  @HostListener('window:resize')
  onResize() {
    const width = window.outerWidth;
    if (width < 450 && this._service.sideNav().visible) {
      this._service.updateSidenav(true, false);
    }
  }
  icons = this._constants.icons;
  typePlaceholder = signal<string | null>(null);
  sideMenus = computed(() => {
    const icons = this.icons();

    if (!icons) return {};  // fallback while loading

    return {
      facebook: [
        {
          name: 'Dashboard',
          link: 'fb/home',
          icon: this._utilService.svgToBase64(icons['home'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['home']['icon-active']
          ),
          enabled: this.isRouteEnabled('home'),
        },
        {
          name: 'Post History',
          link: 'fb/post-history',
          icon: this._utilService.svgToBase64(icons['post-history'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['post-history']['icon-active']
          ),
          enabled: this.isRouteEnabled('post-history'),
        }
      ],
      waba: [
        {
          name: 'Dashboard',
          link: 'wa/home',
          icon: this._utilService.svgToBase64(icons['home'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['home']['icon-active']
          ),
          enabled: this.isRouteEnabled('home'),
        },
        {
          name: 'Manage Templates',
          link: 'wa/templates',
          icon: this._utilService.svgToBase64(icons['templates'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['templates']['icon-active']
          ),
          enabled: this.isRouteEnabled('templates'),
        },
        {
          name: 'Phone Numbers',
          link: 'wa/numbers',
          icon: this._utilService.svgToBase64(icons['numbers'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['numbers']['icon-active']
          ),
          enabled: this.isRouteEnabled('numbers'),
        },
        {
          name: 'Chat History',
          link: 'wa/chat-history',
          icon: this._utilService.svgToBase64(icons['chat-history'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['chat-history']['icon-active']
          ),
          enabled: this.isRouteEnabled('chat-history'),
        },
        {
          name: 'Contacts',
          link: 'wa/customers',
          icon: this._utilService.svgToBase64(icons['customers'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['customers']['icon-active']
          ),
          enabled: this.isRouteEnabled('customers'),
        },
        {
          name: 'Campaigns',
          link: 'wa/campaigns',
          icon: this._utilService.svgToBase64(icons['campaigns'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['campaigns']['icon-active']
          ),
          enabled: this.isRouteEnabled('campaigns'),
        },
        {
          name: 'Assistants',
          link: 'wa/assistants',
          icon: this._utilService.svgToBase64(icons['assistants'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['assistants']['icon-active']
          ),
          enabled: this.isRouteEnabled('assistants'),
        },
        {
          name: 'Manage Content',
          link: 'wa/contents',
          icon: this._utilService.svgToBase64(icons['contents'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['contents']['icon-active']
          ),
          enabled: this.isRouteEnabled('contents'),
        },
      ],
      voice_agent: [
        {
          name: 'Dashboard',
          link: 'va/home',
          icon: this._utilService.svgToBase64(icons['home'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['home']['icon-active']
          ),
          enabled: this.isRouteEnabled('home'),
        },
        {
          name: 'Phone Numbers',
          link: 'va/phone-numbers',
          icon: this._utilService.svgToBase64(icons['numbers'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['numbers']['icon-active']
          ),
          enabled: this.isRouteEnabled('numbers'),
        },
        {
          name: 'Agents',
          link: 'va/agents',
          icon: this._utilService.svgToBase64(icons['assistants'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['assistants']['icon-active']
          ),
          enabled: this.isRouteEnabled('agents'),
        },
        {
          name: 'Call History',
          link: 'va/call-history',
          icon: this._utilService.svgToBase64(icons['call-history'].icon),
          icon_active: this._utilService.svgToBase64(
            icons['call-history']['icon-active']
          ),
          enabled: this.isRouteEnabled('call-history'),
        }
      ],
    }
  });
  menus = signal<any[]>([]);
  FbPages = this._service.pages;
  wabas = this._service.wabas;
  voice_agent = this._service.voice_agent;
  showSubMenu = signal(false);
  currentUrl = toSignal(
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      startWith(this._router.url)
    ),
    { initialValue: this._router.url }
  );

  constructor() {
    effect(() => {
      if (this.selectedAsset()?.type) {
        this.handleTypeChange(this.selectedAsset(), false);
        this.loadSubMenu();
      }
    })
  }

  ngOnInit() {
    this.getBusinessAssets();
    this.initializeRoutingLoader();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  initializeRoutingLoader() {
    this.routerSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLoading.set(false);
        this.currentRoute.set(event.url.substring(1));

        if (this.isMobileDevice() && this.sidenav().isExtended) {
          this.toggleSidenav();
        }
      } else if (event instanceof NavigationError) {
        this.isLoading.set(false);
        this.currentRoute.set(null);
        this._toastr.error(`Kindly refresh the page to update.`);
      }
    });
  }

  private isMobileDevice(): boolean {
    return (
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
  }

  loadSubMenu() {
    const asset: any = this.selectedAsset();
    this.showSubMenu.set(asset?.id || (asset?.type === 'voice_agent' && asset?.activated));
  }

  setLoading(route: any, event: MouseEvent): void {
    if (route.enabled == false) return;
    event.preventDefault();
    if (this.currentRoute() == route.link) return;

    this.isLoading.set(true);
    this.currentRoute.set(route.link);

    setTimeout(() => {
      this._router.navigate([route.link]);
    }, 500);
  }

  linkAccount(type: 'facebook' | 'waba') {
    this._service.setActiveAsset({ id: '', type });
    this._router.navigate(['/link-account'])
  }

  getBusinessAssets() {
    this._apiService.getBusinessAssets().subscribe({
      next: (data: any) => {
        const wabas = data.response.wabas ?? [];
        const fbPages = data.response.pages ?? [];
        const voiceAgent = data.response.voice_agent ?? { type: 'voice_agent', activated: false };
        this._service.updateAssets(wabas, fbPages, voiceAgent);
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        this._toastr.error(err?.error?.message || 'Something went wrong');
      },
    });
  }

  setMenu(menuType: 'facebook' | 'waba' | 'voice_agent') {
    this.menus.set(this.sideMenus()[menuType] ?? []);
  }

  handleAssetChange(item: 'facebook' | 'waba' | 'voice_agent') {
    const assetMap = {
      facebook: () => this.FbPages()?.[0] ?? { type: 'facebook' },
      waba: () => this.wabas()?.[0] ?? { type: 'waba' },
      voice_agent: () => (this.voice_agent() ?? { type: 'voice_agent' }),
    };

    const getAsset = assetMap[item];

    if (!getAsset()) {
      this._toastr.error('Invalid asset type selected');
      return;
    }

    this.handleTypeChange(getAsset(), true);
  }

  handleTypeChange(value: any, redirect: boolean) {
    let placeholder = '';
    switch (value?.type) {
      case 'facebook': {
        placeholder = 'Facebook Page';
        break;
      }
      case 'waba': {
        placeholder = 'WhatsApp Business Account';
        break;
      }
      default:
        placeholder = '';
    }
    this.setMenu(value?.type);
    this.typePlaceholder.set(placeholder);
    this.selectAsset && this.selectAsset.toggle();
    this._service.setActiveAsset(value);
    if (value.type == 'facebook' && !value.id) {
      this._router.navigate(['/link-account']);
      return;
    } else if (value.type == 'waba' && !value.id) {
      this._router.navigate(['/link-account']);
      return;
    } else if (value.type == 'voice_agent' && !value.activated) {
      this._router.navigate(['/link-account']);
      return;
    }
    redirect && this._service.handleRedirect();
  }

  toggleSidenav = () => {
    this._service.updateSidenav(true, !this._service.sideNav().isExtended);
  };

  isRouteEnabled(link: string): boolean {
    const billing = this._storageService.getItem('billing');
    return billing == 'false' && ['assistants', 'fb/assistants'].includes(link)
      ? false
      : true;
  }

  navigate(nav: any) {
    if (!nav.enabled) {
      this.toastMessage();
      return;
    } else {
      this._router.navigate([nav.link]);
    }
  }

  toastMessage() {
    this._toastr.info('Please link your billing account first');
  }
}
